import { useEffect, useRef, useState } from "react";
import { useNavigate, useParams, Link } from "react-router-dom";
import { ArrowLeft, Save, Play, Check, Sparkles, Wand2, Loader2, Pause, PlayCircle, Mic, MicOff, FileText, Gavel, ChevronDown } from "lucide-react";
import { api } from "../lib/api";
import { toast } from "sonner";
import { useAuth } from "../lib/auth";
import useSpeechToText from "../lib/useSpeechToText";
import AIConfirmModal from "../components/AIConfirmModal";
import AutofillModal from "../components/AutofillModal";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "../components/ui/alert-dialog";
import dayjs from "dayjs";

import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { TimePicker } from "@mui/x-date-pickers/TimePicker";

const SOURCE_MODES = [
  { v: "hybrid", label: "Hybrid (recommended) — web search + your source URLs" },
  { v: "web_search", label: "Web search only — Google News and Bing News" },
  { v: "seed_only", label: "Source URLs only — only watch the websites you list below" },
];



// const MIN_SCORES = [0, 10, 20, 30, 40, 45, 50, 60, 70, 80, 90];
const MIN_SCORES = [10, 20, 30, 40, 45, 50, 60, 70, 80];

// ---------------------------------------------------------------------------
// Validation helpers
// ---------------------------------------------------------------------------
const isBlank = (v) => typeof v === "string" && v.trim() === "";
const isEmpty = (v) => !v || (Array.isArray(v) ? v.length === 0 : isBlank(v));

const normalizeUrlValue = (value) => {
  const raw = (value || "").trim();
  if (!raw) return "";
  if (/^https?:\/\//i.test(raw)) return raw;
  if (raw.startsWith("//")) return `https:${raw}`;
  return `https://${raw}`;
};

const isValidWebsiteUrl = (value) => {
  const raw = (value || "").trim();
  if (!raw) return false;

  const candidate = /^https?:\/\//i.test(raw) ? raw : normalizeUrlValue(raw);

  try {
    const parsed = new URL(candidate);
    if (!["http:", "https:"].includes(parsed.protocol)) return false;

    const host = parsed.hostname.toLowerCase();
    if (!host.includes(".")) return false;
    if (host.endsWith(".")) return false;
    if (!/^(?:[a-z0-9-]+\.)+[a-z]{2,}$/i.test(host)) return false;
    return true;
  } catch {
    return false;
  }
};

const handleLimitedInputChange = (
  e,
  values,
  setInput,
  maxItems
) => {
  const count = (values || []).filter(
    (v) => v?.trim()
  ).length;

  if (count >= maxItems) {
    return;
  }

  setInput(e.target.value);
};

function validateForm(form) {
  const errs = {};

  // Project name — required, no blank spaces
  if (isEmpty(form.name)) {
    errs.name = "Project name is required.";
  } else if (isBlank(form.name)) {
    errs.name = "Project name cannot be blank spaces.";
  }

  // P3 #13: Description — strict 500-char limit
  if (form.description && form.description.length > 500) {
    errs.description = `Description must be 500 characters or fewer (currently ${form.description.length}).`;
  }

  // At least one template
  if (!form.template_ids || form.template_ids.length === 0) {
    errs.template_ids = "Select at least one use-case template.";
  }

  // Tender use-case requires 1-3 portals from the server-provided catalog.
  if ((form.template_ids || []).includes("tender_monitoring")) {
    const portals = (form.tender_source_portal_ids || []).filter(Boolean);
    if (portals.length < 1) errs.tender_source_portal_ids = "Select at least one tender source portal.";
    if (portals.length > 3) errs.tender_source_portal_ids = "You can select a maximum of 3 tender source portals.";
  }

  // Source mode — should always have a value (select), but guard anyway
  if (!form.source_mode) {
    errs.source_mode = "Please select a source mode.";
  }

  // Seed URLs — validate each entry looks like a URL
  if (form.seed_urls && form.seed_urls.length > 0) {
    const bad = form.seed_urls.filter((u) => !isValidWebsiteUrl(u));
    if (bad.length > 0) {
      errs.seed_urls = `Invalid URL(s): ${bad.join(", ")}`;
    }
  }

  // Seed-only mode requires at least one URL
  if (form.source_mode === "seed_only" && (!form.seed_urls || form.seed_urls.length === 0)) {
    errs.seed_urls = "Source URLs only mode requires at least one URL.";
  }

  // Primary sectors — required
  if (isEmpty(form.sectors)) {
    errs.sectors = "Add at least one primary sector.";
  }

  const seccount = (form.sectors || []).filter(
    s => s?.trim()
  ).length;


  if (seccount > 2) {
    errs.sectors = "Maximum of 2 primary sectors.";
  }
  // Keywords — required
  if (isEmpty(form.keywords)) {
    errs.keywords = "Add at least one keyword.";
  }

  const kwcount = (form.keywords || []).filter(
    s => s?.trim()
  ).length;

  if (kwcount > 6) {
    errs.keywords = "Maximum of 6 Keywords.";
  }

  const ascount = (form.adjacent_sectors || []).filter(
    s => s?.trim()
  ).length;

  if (ascount > 2) {
    errs.adjacent_sectors = "Maximum of 2 Adjacent sectors.";
  }
  const gfcount = (form.geographic_focus || []).filter(
    s => s?.trim()
  ).length;

  if (gfcount > 2) {
    errs.geographic_focus = "Maximum of 2 Geographic Focus.";
  }
  const kycount = (form.key_companies || []).filter(
    s => s?.trim()
  ).length;

  if (kycount > 2) {
    errs.key_companies = "Maximum of 2 Key Companies.";
  }
  const kyscount = (form.key_stakeholders || []).filter(
    s => s?.trim()
  ).length;

  if (kyscount > 2) {
    errs.key_stakeholders = "Maximum of 2 Key Stake Holders.";
  }
  const cmpcount = (form.competitor_companies || []).filter(
    s => s?.trim()
  ).length;

  if (cmpcount > 2) {
    errs.competitor_companies = "Maximum of 2 Competitor.";
  }

  // Relevance definition — required, no blank
  if (isEmpty(form.relevance_definition)) {
    errs.relevance_definition = "Describe what 'relevant' means for this project.";
  } else if (isBlank(form.relevance_definition)) {
    errs.relevance_definition = "This field cannot be blank spaces.";
  }

  // Commercial importance — required, no blank
  if (isEmpty(form.importance_context)) {
    errs.importance_context = "Explain why this matters commercially.";
  } else if (isBlank(form.importance_context)) {
    errs.importance_context = "This field cannot be blank spaces.";
  }

  // Schedule hour — must be a number 0-23
  if (form.schedule_hour === undefined || form.schedule_hour === null || form.schedule_hour === "") {
    errs.schedule_hour = "Select a daily run hour.";
  }

  // Timezone — required
  if (isEmpty(form.schedule_timezone)) {
    errs.schedule_timezone = "Select a timezone.";
  }

  // Min relevance score — must be a number
  if (form.min_relevance_score === undefined || form.min_relevance_score === null || form.min_relevance_score === "") {
    errs.min_relevance_score = "Select a minimum relevance score.";
  }

  return errs;
}

// ---------------------------------------------------------------------------
// Sub-components
// ---------------------------------------------------------------------------
const ErrorMsg = ({ msg }) =>
  msg ? <p className="mt-1.5 text-xs text-[#B91C1C]">{msg}</p> : null;

const TagInput = ({
  value,
  onChange,
  placeholder,
  testid,
  error,
  maxTags = Infinity,
  correctOnEnter = false,
}) => {
  const [draft, setDraft] = useState("");
  const [correcting, setCorrecting] = useState(false);

  const tags = (value || []).filter((t) => t?.trim());

  const add = () => {
    // Split comma-separated input into individual tags so pasting or typing
    // "solar, wind, hydro" becomes three tags, not one. Fixes the keyword
    // comma-parsing bug at the source.
    const parts = draft
      .split(",")
      .map((s) => s.trim())
      .filter(Boolean);
    if (parts.length === 0) {
      setDraft("");
      return;
    }

    let current = [...tags];
    for (const p of parts) {
      if (current.length >= maxTags) break;
      if (current.some((x) => x.toLowerCase() === p.toLowerCase())) continue;
      current.push(p);
    }
    onChange(current);
    setDraft("");
  };

  // Phase 1 — spell/grammar-correct a single keyword when the user presses Enter
  // (Space already benefits from the browser's native autocorrect; Enter did not).
  const addWithCorrection = async () => {
    const d = draft.trim();
    if (!d || d.includes(",") || tags.length >= maxTags) { add(); return; }
    if (tags.some((x) => x.toLowerCase() === d.toLowerCase())) { setDraft(""); return; }
    setCorrecting(true);
    try {
      const r = await api.post("/ai/correct-keyword", { keyword: d });
      const corrected = (r.data?.corrected || d).trim();
      if (!tags.some((x) => x.toLowerCase() === corrected.toLowerCase()) && tags.length < maxTags) {
        onChange([...tags, corrected]);
      }
      setDraft("");
      if (r.data?.changed) toast.success(`Corrected to "${corrected}"`);
    } catch {
      add();
    } finally {
      setCorrecting(false);
    }
  };

  return (
    <div data-testid={testid}>
      {tags.length > 0 && (
        <div className="flex flex-wrap gap-2 mb-2">
          {tags.map((t, i) => (
            <span
              key={`${t}-${i}`}
              title={t}
              className="inline-flex items-center gap-2 px-2.5 py-1.5 rounded-md bg-[#F1F5F9] text-xs text-[#0F1F3D] max-w-[220px]"
            >
              <span className="font-mono text-[11px] leading-tight">
                {t.length > 15 ? `${t.slice(0, 15)}...` : t}
              </span>

              <button
                type="button"
                onClick={() => onChange(tags.filter((_, idx) => idx !== i))}
                className="text-[#94A3B8] hover:text-[#B91C1C] text-base leading-none relative -top-[2px]"
              >
                ×
              </button>
            </span>
          ))}
        </div>
      )}

      <input
        value={draft}
        onChange={(e) => {
          // Prevent typing once limit is reached
          if (tags.length >= maxTags) return;
          setDraft(e.target.value);
        }}
        onKeyDown={(e) => {
          if (e.key === ",") {
            e.preventDefault();
            add();
          } else if (e.key === "Enter") {
            e.preventDefault();
            if (correctOnEnter) addWithCorrection();
            else add();
          }
        }}
        onBlur={add}
        placeholder={placeholder}
        disabled={correcting}
        className={`w-full px-3 py-2.5 rounded-md border text-sm focus:outline-none focus:ring-2 focus:ring-[#0F1F3D] ${error
          ? "border-[#B91C1C] bg-[#FFF5F5]"
          : "border-[#E2E8F0]"
          }`}
      />
      {correcting && (
        <div className="mt-1.5 inline-flex items-center gap-1.5 text-xs text-[#475569]">
          <Loader2 className="h-3 w-3 animate-spin" /> Checking spelling…
        </div>
      )}
    </div>
  );
};

const Field = ({ label, hint, children, error }) => (
  <div className="space-y-2.5" data-error={error ? "true" : undefined}>
    <label className="type-eyebrow text-[#64748B] block">{label}</label>
    {hint && <div className="type-caption text-[#94A3B8]">{hint}</div>}
    <div>{children}</div>
    <ErrorMsg msg={error} />
  </div>
);

const getUserTimezone = () => {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone || "UTC";
  } catch {
    return "UTC";
  }
};

const empty = {
  name: "", template_ids: ["news_monitoring"], description: "",
  context: "",
  sectors: [], adjacent_sectors: [],
  keywords: [], excluded_keywords: [],
  key_companies: [], competitor_companies: [], partnership_targets: [], key_stakeholders: [],
  geographic_focus: [], importance_context: "", relevance_definition: "",
  seed_urls: [], source_mode: "hybrid",
  tender_source_portal_ids: [], tender_source_urls: [],
  max_article_age_days: 7,
  schedule_hour: 8, schedule_minute: 0, schedule_timezone: getUserTimezone(),
  min_relevance_score: 45,
  is_active: true,
};


// ---------------------------------------------------------------------------
// Main component
// ---------------------------------------------------------------------------
// P3#3 — "Test this keyword": show which recent headlines a keyword would have caught.
//REMOVED -> KeywordTester feature removed per user request.
/*
function KeywordTester() {
  const [kw, setKw] = useState("");
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);

  const run = async () => {
    const q = kw.trim();
    if (q.length < 2) return;
    setLoading(true);
    setResult(null);
    try {
      const r = await api.post("/ai/test-keyword", { keyword: q, days: 7 });
      setResult(r.data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Keyword test failed");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="mt-3 rounded-lg border border-[#E2E8F0] bg-[#F8FAFC] p-3" data-testid="keyword-tester">
      <div className="text-[11px] font-bold uppercase tracking-wide text-[#64748B] mb-2">
        Test a keyword — what it would have caught this week
      </div>
      <div className="flex gap-2">
        <input
          value={kw}
          onChange={(e) => setKw(e.target.value)}
          onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); run(); } }}
          placeholder="e.g. development agreement"
          data-testid="keyword-tester-input"
          className="flex-1 text-sm px-3 py-2 rounded-md border border-[#CBD5E1] bg-white focus:outline-none focus:ring-2 focus:ring-[#0F1F3D]/20 min-h-[44px]"
        />
        <button
          type="button"
          onClick={run}
          disabled={loading}
          data-testid="keyword-tester-run"
          className="px-4 py-2 rounded-md bg-[#0F1F3D] text-white text-sm font-semibold hover:bg-[#1E293B] disabled:opacity-50 min-h-[44px]"
        >
          {loading ? "Testing…" : "Test"}
        </button>
      </div>
      {result && (
        <div className="mt-3" data-testid="keyword-tester-result">
          <div className="text-xs text-[#475569] mb-2">
            <span className="font-semibold">{result.count}</span> match{result.count === 1 ? "" : "es"} in the past {result.days} days
            {result.count > result.matches.length ? ` (showing ${result.matches.length})` : ""}
          </div>
          {result.matches.length === 0 ? (
            <div className="text-sm text-[#94A3B8]">No recent headlines matched — try a broader or trade-press phrasing.</div>
          ) : (
            <ul className="space-y-1.5">
              {result.matches.map((m, i) => (
                <li key={i} className="text-sm text-[#0F1F3D] flex items-start gap-2">
                  <span className="shrink-0 text-[11px] font-bold text-[#0F1F3D] mt-0.5">{m.relevance_score}</span>
                  <a href={m.url} target="_blank" rel="noreferrer" className="hover:underline break-words">
                    {m.title} <span className="text-[#94A3B8]">· {m.source}</span>
                  </a>
                </li>
              ))}
            </ul>
          )}
        </div>
      )}
    </div>
  );
}
*/


export default function ProjectForm() {
  const { id } = useParams();
  const isEdit = !!id;
  const { user } = useAuth();
  const isOrgMember = user?.role === "org_member";
  const nav = useNavigate();
  const [form, setForm] = useState(empty);
  const [templates, setTemplates] = useState([]);
  const [tenderPortals, setTenderPortals] = useState([]);
  const [tenderPortalDropdownOpen, setTenderPortalDropdownOpen] = useState(false);
  const tenderPortalRef = useRef(null);
  const [timezones, setTimezones] = useState([]);
  const [busy, setBusy] = useState(false);
  const [runConfirmOpen, setRunConfirmOpen] = useState(false);
  const [errors, setErrors] = useState({});
  const [submitted, setSubmitted] = useState(false); // track first submit attempt
  // P3 — AI assist busy flags
  const [aiBusy, setAiBusy] = useState({ refine: false, autofill: false });
  // P1#2 — AI-text confirmation modal (nothing AI-written applies without Accept)
  const [aiModal, setAiModal] = useState({ open: false });
  const [autofillModal, setAutofillModal] = useState({ open: false, options: null });

  const isSavedRef = useRef(false);
  const formRef = useRef(form);

  useEffect(() => {
    formRef.current = form;
  }, [form]);

  useEffect(() => {
    return () => {
      if (isSavedRef.current || isOrgMember) return;

      const currentForm = formRef.current;
      const trimmedName = (currentForm.name || "").trim();

      // Only auto-save if name is at least 2 characters
      if (trimmedName.length >= 2) {
        const { news_api_key, google_news_api_key, custom_ai, ai_settings, ...clean } = currentForm;
        const payload = { ...clean, name: trimmedName, status: "draft" };

        // For existing projects, only auto-save if it's already a draft.
        // For new projects, always auto-save as draft.
        if (isEdit) {
          if (currentForm.status === "draft") {
            api.patch(`/projects/${id}`, payload).catch(() => {});
          }
        } else {
          api.post("/projects", payload).catch(() => {});
        }
      }
    };
  }, [isEdit, id, isOrgMember]);


  // P7 — Web Speech API for dictating into the Context field
  const speech = useSpeechToText({
    onResult: (chunk) => {
      // Append the dictation to whatever the user has already typed/dictated.
      setForm((f) => {
        const existing = (f.description || "").trim();
        const combined = (existing ? existing + " " : "") + chunk;
        return { ...f, description: combined.slice(0, 4000) };
      });
    },
  });
  const toggleMic = () => {
    if (!speech.supported) {
      toast.error("Speech-to-text is not supported in this browser. Try Chrome, Edge or Safari.");
      return;
    }
    if (speech.listening) speech.stop();
    else speech.start();
  };
  // Surface microphone permission failures
  useEffect(() => {
    if (!speech.error) return;
    if (speech.error === "not-allowed" || speech.error === "service-not-allowed") {
      toast.error("Microphone access denied. Allow it in your browser settings.");
    } else if (speech.error === "no-speech") {
      // Silent — common
    } else if (speech.error !== "not_supported") {
      toast.error(`Speech-to-text: ${speech.error}`);
    }
  }, [speech.error]);

  useEffect(() => {
    if (!tenderPortalDropdownOpen) return;
    const handlePointerDown = (event) => {
      if (tenderPortalRef.current && !tenderPortalRef.current.contains(event.target)) {
        setTenderPortalDropdownOpen(false);
      }
    };
    document.addEventListener("mousedown", handlePointerDown);
    return () => document.removeEventListener("mousedown", handlePointerDown);
  }, [tenderPortalDropdownOpen]);

  useEffect(() => {
    api.get("/templates").then((r) => {
      setTemplates(r.data.templates);
      const tzList = r.data.timezones || [];
      const userTz = getUserTimezone();
      // Ensure user's timezone is available in the dropdown even if backend list doesn't include it
      const hasUserTz = tzList.some((tz) => tz.v === userTz);
      if (userTz !== "UTC" && !hasUserTz) {
        tzList.push({ v: userTz, label: userTz, group: "Other" });
      }
      setTimezones(tzList);
      // For new projects, ensure default reflects user's timezone (in case empty was evaluated before Intl was ready)
      if (!isEdit) {
        setForm((prev) => {
          // Only override if still at default UTC — don't clobber user manual selection or edit data
          if (prev.schedule_timezone === "UTC" && userTz !== "UTC") {
            return { ...prev, schedule_timezone: userTz };
          }
          return prev;
        });
      }
    });
    api.get("/tender-available").then((r) => setTenderPortals(r.data.portals || [])).catch(() => {});
    if (isEdit) {
      api.get(`/projects/${id}`).then((r) => {
        const p = r.data;
        setForm({
          ...empty,
          ...p,
          template_ids: p.template_ids || ["news_monitoring"],
          tender_source_portal_ids: p.tender_source_portal_ids || [],
          sectors: p.sectors || [],
          schedule_hour: p.schedule_hour ?? empty.schedule_hour,
          schedule_minute: p.schedule_minute ?? empty.schedule_minute,
        });
      });
    }
  }, [id, isEdit]);

  const update = (k, v) => {
    setForm((prev) => {
      const next = { ...prev, [k]: v };
      if (submitted) setErrors(validateForm(next));
      return next;
    });
  };

  const toggleTemplate = (tid) => {
    const cur = form.template_ids || [];
    const next = cur.includes(tid) ? cur.filter((x) => x !== tid) : [...cur, tid];
    if (next.length === 0) {
      toast.warning("At least one template must be selected.");
      return;
    }
    update("template_ids", next);
  };

  // P3 — AI Refine for the description field
  const aiRefineDescription = async () => {
    const text = (form.description || "").trim();
    if (text.length < 4) {
      toast.error("Type a few words first — then click AI Refine.");
      return;
    }
    setAiBusy((s) => ({ ...s, refine: true }));
    try {
      const r = await api.post("/ai/refine-description", { text, project_name: form.name });
      const refined = (r.data?.refined || "").slice(0, 500);
      if (refined) {
        // P1#2 — require explicit review/Accept before it replaces the field.
        setAiModal({
          open: true,
          kind: "refine",
          mode: "text",
          title: "Review AI-refined description",
          originalText: text,
          aiText: refined,
        });
      } else {
        toast.error("AI returned an empty response. Try again.");
      }
    } catch (err) {
      toast.error(err?.response?.data?.detail || "AI refine failed");
    } finally {
      setAiBusy((s) => ({ ...s, refine: false }));
    }
  };

  // Phase 2 — AI Auto-fill: open the selectable-chip modal (org-context aware).
  const aiAutofillContext = async () => {
    const ctx = (form.description || "").trim();
    if (ctx.length < 10) {
      toast.error("Add a Description of context first (≥ 10 characters).");
      return;
    }
    setAiBusy((s) => ({ ...s, autofill: true }));
    try {
      const r = await api.post("/ai/autofill-context", { context: ctx, project_name: form.name });
      const data = r.data || {};
      const options = {
        sectors: Array.isArray(data.sectors) ? data.sectors : [],
        keywords: Array.isArray(data.keywords) ? data.keywords : [],
        relevance_definition: data.relevance_definition || "",
        importance_context: data.importance_context || "",
      };
      const hasAny =
        options.sectors.length || options.keywords.length ||
        options.relevance_definition || options.importance_context;
      if (!hasAny) {
        toast.error("AI did not return anything to suggest.");
        return;
      }
      setAutofillModal({ open: true, options });
    } catch (err) {
      toast.error(err?.response?.data?.detail || "AI autofill failed");
    } finally {
      setAiBusy((s) => ({ ...s, autofill: false }));
    }
  };

  // Apply only the fields/chips the user selected in the AutofillModal.
  const applyAutofill = (payload) => {
    const next = { ...form };
    Object.entries(payload || {}).forEach(([k, v]) => {
      if (Array.isArray(v)) next[k] = v;
      else if (typeof v === "string") next[k] = v;
    });
    setForm(next);
    if (submitted) setErrors(validateForm(next));
    setAutofillModal({ open: false, options: null });
    toast.success("Applied — review and save when ready.");
  };

  // P1#2 — apply AI output only after the user accepts it in the modal.
  const handleAiAccept = (payload) => {
    if (aiModal.kind === "refine") {
      update("description", String(payload || "").slice(0, 500));
      toast.success("Description updated");
    } else if (aiModal.kind === "autofill") {
      const next = { ...form };
      Object.entries(payload || {}).forEach(([k, v]) => {
        if (Array.isArray(v)) {
          const existing = Array.isArray(next[k]) ? next[k] : [];
          if (existing.length === 0) next[k] = v;
          else {
            const lower = new Set(existing.map((x) => String(x).toLowerCase()));
            v.forEach((x) => {
              if (!lower.has(String(x).toLowerCase())) next[k] = [...next[k], x];
            });
          }
        } else if (v) {
          next[k] = v;
        }
      });
      setForm(next);
      if (submitted) setErrors(validateForm(next));
      toast.success("Fields applied — review and save when ready.");
    }
    setAiModal({ open: false });
  };

  // P3 — Toggle Active / Paused without deleting
  const toggleActive = async () => {
    if (!isEdit || isOrgMember) return;
    const next = !form.is_active;
    setForm((f) => ({ ...f, is_active: next }));
    try {
      await api.patch(`/projects/${id}`, { is_active: next });
      toast.success(next ? "Project resumed" : "Project paused — pipeline will skip it");
    } catch (err) {
      // revert
      setForm((f) => ({ ...f, is_active: !next }));
      toast.error("Could not update status");
    }
  };

  const submit = async (e) => {
    e.preventDefault();
    return saveProject({ asDraft: false });
  };

  // P7 — Save-as-draft skips validation and writes status='draft'. The
  // scheduler ignores drafts, so the user can come back later and complete.
  const saveAsDraft = async () => {
    return saveProject({ asDraft: true });
  };

  const saveProject = async ({ asDraft }) => {
    if (isOrgMember) return;
    setSubmitted(!asDraft); // drafts skip the inline error highlighting

    const trimmedForm = { ...form, name: (form.name || "").trim() };
    setForm(trimmedForm);

    if (asDraft) {
      // Drafts only need a name (≥ 2 chars) to be saveable.
      if (!trimmedForm.name || trimmedForm.name.length < 2) {
        toast.error("Give your draft a name first (at least 2 characters).");
        return;
      }
    } else {
      const errs = validateForm(trimmedForm);
      if (Object.keys(errs).length > 0) {
        setErrors(errs);
        toast.error("Please fix the highlighted fields before saving.");
        setTimeout(() => {
          const nodes = Array.from(document.querySelectorAll("[data-error='true']"));
          if (nodes.length) {
            const topmost = nodes.reduce((best, el) =>
              el.getBoundingClientRect().top < best.getBoundingClientRect().top ? el : best
            );
            topmost.scrollIntoView({ behavior: "smooth", block: "center" });
            const focusable = topmost.querySelector("input, textarea, select, button");
            if (focusable) setTimeout(() => focusable.focus({ preventScroll: true }), 300);
          }
        }, 50);
        return;
      }
    }

    setBusy(true);
    try {
      const { news_api_key, google_news_api_key, custom_ai, ai_settings, ...clean } = trimmedForm;
      const payload = { ...clean, status: asDraft ? "draft" : "active" };
      if (isEdit) {
        await api.patch(`/projects/${id}`, payload);
        isSavedRef.current = true;
        toast.success(asDraft ? "Draft saved" : "Project saved");
        nav(asDraft ? "/app/projects?tab=drafts" : "/app/projects");
      } else {
        const r = await api.post("/projects", payload);
        isSavedRef.current = true;
        toast.success(asDraft ? "Draft saved" : "Project created");
        nav(asDraft ? "/app/projects?tab=drafts" : `/app/projects/${r.data.id}`);
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    } finally {
      setBusy(false);
    }
  };

  const runNow = async () => {
    if (!isEdit || isOrgMember) return;
    setBusy(true);
    toast.message("Running pipeline");
    try {
      const r = await api.post(`/projects/${id}/run`);
      if (r.data.ok) {
        const extra = r.data.credit_capped ? " (credit limit reached)" : "";
        toast.success(`Done, ${r.data.kept} new results kept${extra}`);
      } else {
        toast.error(r.data.error || r.data.detail || "Please try again");
      }
    } catch (e) {
      toast.error(
        e?.response?.data?.detail ||
        e?.response?.data?.error ||
        "Please try again"
      );
    } finally { setBusy(false); }
  };

  const confirmRun = () => {
    setRunConfirmOpen(false);
    runNow();
  };

  const groupedTimezones = timezones.reduce((acc, tz) => {
    const grp = tz.group || "Other";
    if (!acc[grp]) acc[grp] = [];
    acc[grp].push(tz);
    return acc;
  }, {});

  // Helper: attach data-error attr for scroll-to-first-error
  const errAttr = (key) => errors[key] ? { "data-error": "true" } : {};


  const resetProjectContext = () => {
    if (
      !window.confirm(
        "Clear all project context fields? This cannot be undone."
      )
    ) {
      return;
    }

    setForm((prev) => ({
      ...prev,
      context: "",
      sectors: [],
      adjacent_sectors: [],
      keywords: [],
      excluded_keywords: [],
      key_companies: [],
      competitor_companies: [],
      partnership_targets: [],
      key_stakeholders: [],
      geographic_focus: [],
      relevance_definition: "",
      importance_context: "",
    }));

    setErrors({});
    toast.success("Project context cleared.");
  };

  const hasProjectContext =
    form.context.trim() !== "" ||
    form.sectors.length > 0 ||
    form.adjacent_sectors.length > 0 ||
    form.keywords.length > 0 ||
    form.excluded_keywords.length > 0 ||
    form.key_companies.length > 0 ||
    form.competitor_companies.length > 0 ||
    form.partnership_targets.length > 0 ||
    form.key_stakeholders.length > 0 ||
    form.geographic_focus.length > 0 ||
    form.relevance_definition.trim() !== "" ||
    form.importance_context.trim() !== "";

  const [scheduleTime, setScheduleTime] = useState(dayjs().hour(8).minute(0));

  useEffect(() => {
    if (form.schedule_hour == null) return;
    if (form.schedule_minute == null) return;

    setScheduleTime(
      dayjs()
        .hour(form.schedule_hour)
        .minute(form.schedule_minute)
        .second(0)
    );
  }, [form.schedule_hour, form.schedule_minute]);

  const running = busy || form.is_running;

  const isValidUrl = (url) => isValidWebsiteUrl(url);

  const handleSeedUrlsChange = (urls) => {
    const invalid = urls.filter((url) => !isValidUrl(url));

    if (invalid.length > 0) {
      setErrors((prev) => ({
        ...prev,
        seed_urls: "Please enter valid website URLs only.",
      }));
      return;
    }

    setErrors((prev) => ({
      ...prev,
      seed_urls: "",
    }));

    update("seed_urls", urls);
  };

  return (
    <div className="p-8 lg:p-12 max-w-4xl mx-auto" data-testid="project-form">
      <Link to="/app/projects" className="inline-flex items-center gap-2 type-body-sm text-[#475569] hover:text-[#0F1F3D]">
        <ArrowLeft className="h-4 w-4" /> Back to projects
      </Link>
      <h1 className="type-h1 text-[#0F1F3D] mt-4">
        {isEdit ? (isOrgMember ? "View project" : "Edit project") : "New project"}
      </h1>

      <form onSubmit={submit} noValidate className="mt-12 space-y-10">
        <fieldset
          disabled={isOrgMember}
          className={`m-0 min-w-0 space-y-10 border-0 p-0 ${isOrgMember ? "opacity-80" : ""}`}
        >

        {/* ── Basics ── */}
        <section className="bg-white border border-[#E2E8F0] rounded-lg p-8 space-y-7">
          <div>
            <div className="type-eyebrow text-[#0F1F3D]">Basics</div>
            <div className="type-body text-[#64748B] mt-2">Name your project and pick what you want to monitor.</div>
          </div>

          {/* Project name */}
          <div className="space-y-2.5" {...errAttr("name")}>
            <label className="type-eyebrow text-[#64748B] block">Project name <span className="text-[#B91C1C]">*</span></label>
            <input
              required
              value={form.name}
              onChange={(e) => update("name", e.target.value)}
              data-testid="project-name-input"
              className={`w-full px-3 py-2.5 rounded-md border focus:outline-none focus:ring-2 focus:ring-[#0F1F3D] ${errors.name ? "border-[#B91C1C] bg-[#FFF5F5]" : "border-[#E2E8F0]"}`}
            />
            <ErrorMsg msg={errors.name} />
          </div>

          {/* Templates */}
          <div className="space-y-2.5" {...errAttr("template_ids")}>
            <label className="type-eyebrow text-[#64748B] block">
              Use Case Templates <span className="text-[#B91C1C]">*</span>
            </label>
            <div className="type-caption text-[#94A3B8]">Pick one or more. The pipeline runs each and merges results into a single ranked feed.</div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
              {templates.map((t) => {
                const activeT = (form.template_ids || []).includes(t.id);

                const isDisabled =
                  t.label === "Tenders" || t.label === "Regulatory";

                return (
                  <button
                    key={t.id}
                    type="button"
                    disabled={isDisabled}
                    onClick={() => !isDisabled && toggleTemplate(t.id)}
                    data-testid={`template-${t.id}`}
                    className={`text-left p-4 rounded-md border transition-colors relative ${activeT
                      ? "border-[#0F1F3D] bg-[#F8F9FA] ring-2 ring-[#0F1F3D]/10"
                      : "border-[#E2E8F0]"
                      } ${isDisabled
                        ? "opacity-50 cursor-not-allowed bg-[#F8FAFC]"
                        : "hover:border-[#CBD5E1] cursor-pointer"
                      }`}
                  >
                    {activeT && (
                      <div className="absolute top-2 right-2 h-5 w-5 rounded-full bg-[#0F1F3D] text-white flex items-center justify-center">
                        <Check className="h-3 w-3" />
                      </div>
                    )}

                    <div
                      className="text-[10px] uppercase tracking-[0.2em] font-bold"
                      style={{ color: t.color }}
                    >
                      {t.label}
                    </div>

                    <div className="font-display font-bold text-[#0F1F3D] mt-1">
                      {t.label}
                    </div>

                    <div className="text-xs text-[#64748B] mt-1">
                      {t.tagline}
                    </div>

                    {isDisabled && (
                      <div className="mt-3 inline-flex items-center rounded-full border border-[#E2E8F0] bg-white px-2 py-1 text-[10px] font-medium text-[#64748B]">
                        Coming Soon
                      </div>
                    )}
                  </button>
                );
              })}
            </div>
            <ErrorMsg msg={errors.template_ids} />
          </div>

          {/* P3 (Round 2) — Tender source portals (mandatory when Tenders is on) */}
          {(form.template_ids || []).includes("tender_monitoring") && (
            <div className="rounded-2xl border border-[#B45309]/30 bg-[#FFFBEB] p-5 sm:p-6" data-testid="tender-sources-panel" {...errAttr("tender_source_portal_ids")}>
              <div className="flex items-center gap-2">
                <Gavel className="h-4 w-4 text-[#B45309]" />
                <span className="text-sm font-bold text-[#0F1F3D]">Tender source portals</span>
                <span className="text-[#B91C1C]">*</span>
              </div>
              <p className="text-xs text-[#92400E] mt-1.5" data-testid="tender-scope-note">
                Currently covers <strong>Indian government tenders only</strong>. Select up to 3 portals to crawl.
              </p>
              <div className="mt-3" data-testid="project-tender-portals-input">
                {(form.tender_source_portal_ids || []).length > 0 && (
                  <div className="flex flex-wrap gap-2 mb-2">
                    {(form.tender_source_portal_ids || []).map((portalId) => {
                      const portal = tenderPortals.find((item) => item.id === portalId);
                      if (!portal) return null;
                      return (
                        <span key={portal.id} title={portal.url} className="inline-flex items-center gap-2 rounded-md bg-[#F1F5F9] px-2.5 py-1.5 text-xs text-[#0F1F3D]">
                          <span className="min-w-0">
                            <span className="block max-w-[260px] truncate font-semibold">{portal.name}</span>
                            <span className="block max-w-[260px] truncate text-[11px] text-[#64748B]">{portal.url}</span>
                          </span>
                          <button
                            type="button"
                            onClick={() => update("tender_source_portal_ids", (form.tender_source_portal_ids || []).filter((id) => id !== portal.id))}
                            aria-label={`Remove ${portal.name}`}
                            className="text-base leading-none text-[#94A3B8] hover:text-[#B91C1C]"
                          >
                            ×
                          </button>
                        </span>
                      );
                    })}
                  </div>
                )}
                <div className="relative" ref={tenderPortalRef}>
                  <button
                    type="button"
                    onClick={() => setTenderPortalDropdownOpen((open) => !open)}
                    data-testid="tender-portals-dropdown"
                    className={`flex min-h-[44px] w-full items-center justify-between rounded-md border bg-white px-3 py-2.5 text-left text-sm focus:outline-none focus:ring-2 focus:ring-[#0F1F3D] ${errors.tender_source_portal_ids ? "border-[#B91C1C]" : "border-[#E2E8F0]"}`}
                  >
                    <span className="text-[#64748B]">Select tender portals</span>
                    <ChevronDown className={`h-4 w-4 text-[#64748B] transition-transform ${tenderPortalDropdownOpen ? "rotate-180" : ""}`} />
                  </button>
                  {tenderPortalDropdownOpen && (
                    <div className="absolute z-20 mt-1 max-h-64 w-full overflow-y-auto rounded-md border border-[#E2E8F0] bg-white p-1 shadow-lg">
                      {tenderPortals.map((portal) => {
                        const selected = (form.tender_source_portal_ids || []).includes(portal.id);
                        const disabled = !selected && (form.tender_source_portal_ids || []).length >= 3;
                        return (
                          <button
                            key={portal.id}
                            type="button"
                            disabled={disabled}
                            onClick={() => {
                              const ids = form.tender_source_portal_ids || [];
                              update("tender_source_portal_ids", selected ? ids.filter((id) => id !== portal.id) : [...ids, portal.id]);
                            }}
                            data-testid={`tender-portal-${portal.id}`}
                            className={`flex w-full items-start gap-2 rounded-md px-3 py-2 text-left ${disabled ? "cursor-not-allowed opacity-40" : "hover:bg-[#FFFBEB]"}`}
                          >
                            <span className={`mt-0.5 flex h-4 w-4 shrink-0 items-center justify-center rounded-sm border text-[11px] ${selected ? "border-[#B45309] bg-[#B45309] text-white" : "border-[#CBD5E1]"}`}>{selected ? "✓" : ""}</span>
                            <span className="min-w-0">
                              <span className="block text-sm font-semibold text-[#0F1F3D]">{portal.name}</span>
                              <span className="block truncate text-xs text-[#64748B]">{portal.url}</span>
                            </span>
                          </button>
                        );
                      })}
                      {tenderPortals.length === 0 && <span className="block px-3 py-2 text-sm text-[#64748B]">Loading available portals...</span>}
                    </div>
                  )}
                </div>
              </div>
              <ErrorMsg msg={errors.tender_source_portal_ids} />
            </div>
          )}

          {/* P1#5 — Voice-first: tap-and-talk is the primary way to describe the project */}
          <div className="rounded-2xl border border-[#0F1F3D]/15 bg-gradient-to-b from-[#F1F5F9] to-white p-4 sm:p-5" data-testid="voice-first-panel">
            <div className="type-eyebrow text-[#0F1F3D]">Tell us about your project</div>
            <h3 className="text-base font-bold text-[#0F1F3D] mt-1">Tap and talk — the fastest way</h3>
            <p className="text-xs text-[#64748B] mt-1">
              Speak naturally about: what you do, what you want to track, the companies/schemes that matter, and your goal.
            </p>

            {speech.supported ? (
              <div className="mt-3 flex flex-col items-center">
                <button
                  type="button"
                  onClick={toggleMic}
                  data-testid="voice-first-mic"
                  data-listening={speech.listening ? "true" : "false"}
                  className={`inline-flex flex-col items-center justify-center gap-1 h-20 w-20 rounded-full transition-all shadow-md ${speech.listening
                    ? "bg-[#FEE2E2] text-[#B91C1C] ring-4 ring-[#FCA5A5] animate-pulse"
                    : "bg-[#0F1F3D] text-white hover:bg-[#172A4F]"}`}
                >
                  {speech.listening ? <MicOff className="h-7 w-7" /> : <Mic className="h-7 w-7" />}
                  <span className="text-[10px] font-semibold">{speech.listening ? "Stop" : "Tap to talk"}</span>
                </button>
                <div className="mt-3 text-xs font-medium min-h-[18px]" data-testid="voice-first-state">
                  {speech.listening
                    ? <span className="inline-flex items-center gap-1.5 text-[#B91C1C]"><span className="h-1.5 w-1.5 rounded-full bg-[#EF4444] animate-pulse" /> Listening — speak now</span>
                    : <span className="text-[#94A3B8]">Your words appear in the box below — edit anytime.</span>}
                </div>
              </div>
            ) : (
              <div className="mt-4 rounded-lg bg-[#FEF3C7] text-[#92400E] text-sm px-4 py-3" data-testid="voice-first-fallback">
                Voice input isn&apos;t available in this browser. No problem — just type your project description below.
              </div>
            )}
          </div>

          {/* Description — type it (secondary path), max 500 chars with AI Refine */}
          <div className="space-y-2.5" {...errAttr("description")}>
            <div className="flex items-center justify-between">
              <label className="type-eyebrow text-[#64748B] block">Or type it instead</label>
              <button
                type="button"
                onClick={aiRefineDescription}
                disabled={aiBusy.refine}
                data-testid="ai-refine-description"
                className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-md border border-[#10B981]/40 bg-[#ECFDF5] text-[#047857] text-[11px] font-bold uppercase tracking-[0.12em] hover:bg-[#D1FAE5] disabled:opacity-60"
                title="Use AI to tighten this description"
              >
                {aiBusy.refine ? <Loader2 className="h-3 w-3 animate-spin" /> : <Wand2 className="h-3 w-3" />}
                Improve Wording
              </button>
            </div>
            <div className="type-caption text-[#94A3B8]">
              Type freely - then select “Improve wording” to tighten the phrasing.
              {" "}
              <span className="font-mono-tab">{(form.description || "").length} / 500</span>
            </div>
            <div className="relative">
              <textarea
                rows={2}
                maxLength={500}
                value={form.description}
                placeholder="e.g. Track EU-India FTA negotiation updates, official trade-policy releases, and competitor reactions in the renewable-energy supply chain."
                onChange={(e) => update("description", e.target.value.slice(0, 500))}
                data-testid="project-description-input"
                className={`w-full px-3 py-2.5 rounded-md border text-sm focus:outline-none focus:ring-2 focus:ring-[#0F1F3D] ${errors.description ? "border-[#B91C1C] bg-[#FFF5F5]" : "border-[#E2E8F0]"}`}
              />
              {/* [REMOVED] Mic button removed from description textarea — the voice-first panel mic above already covers this.
              {speech.supported && (
                <button
                  type="button"
                  onClick={toggleMic}
                  data-testid="context-mic-toggle"
                  data-listening={speech.listening ? "true" : "false"}
                  title={speech.listening ? "Stop dictation" : "Dictate context (speech-to-text)"}
                  className={`absolute top-2 right-2 inline-flex items-center justify-center h-8 w-8 rounded-full transition-colors ${speech.listening
                    ? "bg-[#FEE2E2] text-[#B91C1C] hover:bg-[#FECACA]"
                    : "bg-[#F1F5F9] text-[#475569] hover:bg-[#E2E8F0]"
                    }`}
                >
                  {speech.listening ? <MicOff className="h-4 w-4" /> : <Mic className="h-4 w-4" />}
                </button>
              )}
              */}

            </div>
            {speech.listening && (
              <div className="mt-2 inline-flex items-center gap-1.5 text-xs text-[#B91C1C]" data-testid="context-mic-listening">
                <span className="h-1.5 w-1.5 rounded-full bg-[#EF4444] animate-pulse" />
                Listening — speak now. Click the mic to stop.
              </div>
            )}
            <ErrorMsg msg={errors.description} />
          </div>
        </section>

        {/* ── Sources ── */}
        <section className="bg-white border border-[#E2E8F0] rounded-lg p-8 space-y-7">
          <div>
            <div className="type-eyebrow text-[#0F1F3D]">Sources</div>
            <div className="type-body text-[#64748B] mt-2">Where the pipeline looks for content. LaunchRadar scrapes the open web, no external news API keys required.</div>
          </div>

          <Field label={<>Source mode <span className="text-[#B91C1C]">*</span></>} error={errors.source_mode}>
            <div className="relative">
              <select
                value={form.source_mode}
                onChange={(e) => update("source_mode", e.target.value)}
                data-testid="project-sourcemode-select"
                className={`
      w-full
      appearance-none
      rounded-xl
      border
      bg-white
      px-4
      py-3
      pr-10
      text-sm
      text-[#0F172A]
      shadow-sm
      transition-all
      ${errors.source_mode
                    ? "border-[#DC2626] bg-[#FEF2F2]"
                    : "border-[#E2E8F0]"
                  }
    `}
              >
                {SOURCE_MODES.map((s) => (
                  <option
                    key={s.v}
                    value={s.v}
                    className="bg-white text-[#0F172A]"
                  >
                    {s.label}
                  </option>
                ))}
              </select>

              <svg
                className="pointer-events-none absolute right-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-500"
                viewBox="0 0 20 20"
                fill="currentColor"
              >
                <path
                  fillRule="evenodd"
                  d="M5.23 7.21a.75.75 0 011.06.02L10 11.17l3.71-3.94a.75.75 0 111.08 1.04l-4.25 4.5a.75.75 0 01-1.08 0l-4.25-4.5a.75.75 0 01.02-1.06z"
                  clipRule="evenodd"
                />
              </svg>
            </div>
          </Field>

          {form.source_mode !== "web_search" && (
            <Field
              label="Source URLs (websites to monitor)"
              hint="Add URLs the pipeline should scrape directly. Required when using Source URLs only mode."
              error={errors.seed_urls}
            >
              <TagInput
                value={form.seed_urls}
                onChange={handleSeedUrlsChange}
                placeholder="https://example.gov"
                testid="project-seedurls-input"
                error={!!errors.seed_urls}
              />
            </Field>
          )}
        </section>

        {/* ── Project context ── */}
        <section className="bg-white border border-[#E2E8F0] rounded-lg p-8 space-y-7">
          <div className="flex items-start justify-between gap-4 flex-wrap">
            <div>
              <div className="type-eyebrow text-[#0F1F3D]">Project context</div>
              <div className="type-body text-[#64748B] mt-2">The more specific this is, the more accurate scoring becomes.</div>
            </div>
            <div className="flex gap-2">
              <button
                type="button"
                onClick={aiAutofillContext}
                disabled={aiBusy.autofill}
                data-testid="ai-autofill-context"
                className="inline-flex items-center gap-2 px-3 py-2 rounded-md bg-[#0F1F3D] text-white text-xs font-semibold uppercase tracking-[0.12em] hover:bg-[#172A4F] disabled:opacity-60"
                title="Use AI to fill the project context fields below."
              >
                {aiBusy.autofill ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Sparkles className="h-3.5 w-3.5" />}
                {aiBusy.autofill ? "Filling…" : "AI Auto-fill"}
              </button>

              <button
                type="button"
                onClick={resetProjectContext}
                disabled={!hasProjectContext}
                className={`inline-flex items-center gap-2 px-3 py-2 rounded-md text-xs font-semibold uppercase tracking-[0.12em] transition
    ${hasProjectContext
                    ? "border border-[#E2E8F0] text-[#475569] hover:border-[#B91C1C] hover:text-[#B91C1C]"
                    : "border border-[#E2E8F0] text-[#CBD5E1] cursor-not-allowed opacity-50"
                  }`}
              >
                Reset
              </button>
            </div>
          </div>

          {/* P3 — free-text Context input that powers AI Auto-fill. P7 — mic for dictation. */}
          {/* <Field
            label="Context (free-form)"
            hint="Paragraph describing your business, what you're tracking and why. Click AI Auto-fill above to populate every mandatory field below from this. Use the mic to dictate."
          >
            <div className="relative">
              <textarea
                rows={3}
                maxLength={4000}
                value={form.context}
                placeholder="e.g. We're a Dutch–Indian JV expanding solar PV manufacturing. Track government subsidies in India (MNRE) and the Netherlands (Rijksoverheid), competitor capacity moves (LONGi, Trina, Jinko), and grid-policy changes that affect rooftop and utility-scale PV."
                onChange={(e) => update("context", e.target.value.slice(0, 4000))}
                data-testid="project-context-input"
                className="w-full pr-12 px-3 py-2.5 rounded-md border border-[#E2E8F0] text-sm focus:outline-none focus:ring-2 focus:ring-[#0F1F3D]"
              />
              {speech.supported && (
                <button
                  type="button"
                  onClick={toggleMic}
                  data-testid="context-mic-toggle"
                  data-listening={speech.listening ? "true" : "false"}
                  title={speech.listening ? "Stop dictation" : "Dictate context (speech-to-text)"}
                  className={`absolute top-2 right-2 inline-flex items-center justify-center h-8 w-8 rounded-full transition-colors ${speech.listening
                    ? "bg-[#FEE2E2] text-[#B91C1C] hover:bg-[#FECACA]"
                    : "bg-[#F1F5F9] text-[#475569] hover:bg-[#E2E8F0]"
                    }`}
                >
                  {speech.listening ? <MicOff className="h-4 w-4" /> : <Mic className="h-4 w-4" />}
                </button>
              )}
            </div>
            {speech.listening && (
              <div className="mt-2 inline-flex items-center gap-1.5 text-xs text-[#B91C1C]" data-testid="context-mic-listening">
                <span className="h-1.5 w-1.5 rounded-full bg-[#EF4444] animate-pulse" />
                Listening — speak now. Click the mic to stop.
              </div>
            )}
          </Field> */}

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <Field label={<>Primary sectors <span className="text-[#B91C1C]">*</span></>} hint="What industries the project lives in. (Max 2)" error={errors.sectors} >
              <TagInput value={form.sectors} onChange={(v) => update("sectors", v)} placeholder="e.g. Renewable Energy" testid="project-sectors-input" error={!!errors.sectors} maxTags={2} />
            </Field>
            <Field label="Adjacent sectors" hint="Related industries to also watch. (Max 2)" error={errors.adjacent_sectors}>
              <TagInput value={form.adjacent_sectors} onChange={(v) => update("adjacent_sectors", v)} placeholder="e.g. Energy storage" testid="project-adjacent-input" maxTags={2} />
            </Field>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <Field hint="Add 2–6 or more relevant keywords." label={<>Keywords <span className="text-[#B91C1C]">*</span></>} error={errors.keywords}>
                <TagInput value={form.keywords} onChange={(v) => update("keywords", v)} placeholder="e.g. solar PV" testid="project-keywords-input" error={!!errors.keywords} maxTags={6} correctOnEnter />
              </Field>
              {/*<KeywordTester /> REMOVED -> "Test a keyword" feature removed per user request. */}
            </div>

            <div>
              <Field label="Excluded keywords" hint="Items containing these words score near zero." error={errors.excluded_keywords}>
                <TagInput value={form.excluded_keywords} onChange={(v) => update("excluded_keywords", v)} placeholder="words to ignore" testid="project-excluded-input" />
              </Field>
            </div>
          </div>



          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <Field label="Key companies / entities to track" hint="(Max 2)" error={errors.key_companies}>
              <TagInput value={form.key_companies} onChange={(v) => update("key_companies", v)} placeholder="e.g. Tata Power" testid="project-companies-input" maxTags={2} />
            </Field>
            <Field label="Competitor companies" hint="(Max 2)" error={errors.competitor_companies}>
              <TagInput value={form.competitor_companies} onChange={(v) => update("competitor_companies", v)} placeholder="e.g. LONGi" testid="project-competitors-input" maxTags={2} />
            </Field>
            {/* P3 — Partnership targets temporarily hidden (commented for future).
            <Field label="Partnership targets" error={errors.partnership_targets}>
              <TagInput value={form.partnership_targets} onChange={(v) => update("partnership_targets", v)} placeholder="e.g. Adani Solar" testid="project-partners-input" />
            </Field>
            */}
            <Field label="Key stakeholders / regulatory bodies" hint="(Max 2)" error={errors.key_stakeholders}>
              <TagInput value={form.key_stakeholders} onChange={(v) => update("key_stakeholders", v)} placeholder="e.g. MNRE" testid="project-stakeholders-input" maxTags={2} />
            </Field>
            <Field label="Geographic focus (countries / states)" hint="(Max 2)" error={errors.geographic_focus}>
              <TagInput value={form.geographic_focus} onChange={(v) => update("geographic_focus", v)} placeholder="e.g. Netherlands / India / Maharashtra / Gujarat" testid="project-geo-input" maxTags={2} />
            </Field>
          </div>

          <Field
            label={<>What &apos;relevant&apos; means <span className="text-[#B91C1C]">*</span></>}
            hint="Describe what a relevant result looks like in plain English."
            error={errors.relevance_definition}
          >
            <textarea
              rows={3}
              value={form.relevance_definition}
              onChange={(e) => update("relevance_definition", e.target.value)}
              data-testid="project-relevance-input"
              className={`w-full px-3 py-2.5 rounded-md border text-sm focus:outline-none focus:ring-2 focus:ring-[#0F1F3D] ${errors.relevance_definition ? "border-[#B91C1C] bg-[#FFF5F5]" : "border-[#E2E8F0]"}`}
            />
          </Field>

          <Field
            label={<>Why this matters commercially <span className="text-[#B91C1C]">*</span></>}
            hint="The business angle. Used by the scorer to weigh impact."
            error={errors.importance_context}
          >
            <textarea
              rows={2}
              value={form.importance_context}
              onChange={(e) => update("importance_context", e.target.value)}
              data-testid="project-importance-input"
              className={`w-full px-3 py-2.5 rounded-md border text-sm focus:outline-none focus:ring-2 focus:ring-[#0F1F3D] ${errors.importance_context ? "border-[#B91C1C] bg-[#FFF5F5]" : "border-[#E2E8F0]"}`}
            />
          </Field>
        </section>

        {/* ── Schedule & tuning ── */}
        <section className="bg-white border border-[#E2E8F0] rounded-lg p-8 space-y-7">
          <div>
            <div className="type-eyebrow text-[#0F1F3D]">Schedule and tuning</div>
            <div className="type-body text-[#64748B] mt-2">Choose when this project runs and the minimum relevance score.</div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <Field
              label={
                <>
                  Daily run time <span className="text-[#B91C1C]">*</span>
                </>
              }
              error={errors.schedule_hour}
            >
              <div className="flex items-stretch gap-2" data-testid="project-time-picker">
                {(() => {
                  const h24 = Number(form.schedule_hour ?? 8);
                  const min = Number(form.schedule_minute ?? 0);
                  const period = h24 >= 12 ? "PM" : "AM";
                  const h12 = h24 % 12 === 0 ? 12 : h24 % 12;
                  const setTime = (nh12, nmin, nperiod) => {
                    let hh = nh12 % 12;
                    if (nperiod === "PM") hh += 12;
                    setForm((prev) => ({ ...prev, schedule_hour: hh, schedule_minute: nmin }));
                  };
                  const selCls = `flex-1 px-3 py-2.5 rounded-md border bg-white text-sm focus:outline-none focus:ring-2 focus:ring-[#0F1F3D] ${errors.schedule_hour ? "border-[#B91C1C] bg-[#FFF5F5]" : "border-[#E2E8F0]"}`;
                  return (
                    <>
                      <select data-testid="schedule-hour-select" value={h12} onChange={(e) => setTime(parseInt(e.target.value), min, period)} className={selCls}>
                        {Array.from({ length: 12 }, (_, i) => i + 1).map((h) => (
                          <option key={h} value={h}>{String(h).padStart(2, "0")}</option>
                        ))}
                      </select>
                      <span className="flex items-center text-[#64748B] font-semibold">:</span>
                      <select data-testid="schedule-minute-select" value={min} onChange={(e) => setTime(h12, parseInt(e.target.value), period)} className={selCls}>
                        {Array.from({ length: 12 }, (_, i) => i * 5).map((m) => (
                          <option key={m} value={m}>{String(m).padStart(2, "0")}</option>
                        ))}
                      </select>
                      <select data-testid="schedule-period-select" value={period} onChange={(e) => setTime(h12, min, e.target.value)} className={selCls}>
                        <option value="AM">AM</option>
                        <option value="PM">PM</option>
                      </select>
                    </>
                  );
                })()}
              </div>
              <div className="type-caption text-[#94A3B8] mt-1.5">Project runs once daily around this time, in the timezone you pick.</div>
            </Field>

            <Field label={<>Timezone <span className="text-[#B91C1C]">*</span></>} error={errors.schedule_timezone}>
              <select value={form.schedule_timezone} onChange={(e) => update("schedule_timezone", e.target.value)} data-testid="project-timezone-select"
                className={`w-full px-3 py-2.5 rounded-md border bg-white text-sm ${errors.schedule_timezone ? "border-[#B91C1C] bg-[#FFF5F5]" : "border-[#E2E8F0]"}`}>
                {Object.entries(groupedTimezones).map(([grp, tzs]) => (
                  <optgroup key={grp} label={grp}>
                    {tzs.map((tz) => <option key={tz.v} value={tz.v}>{tz.label}</option>)}
                  </optgroup>
                ))}
              </select>
            </Field>

            <Field label={<>Min relevance score <span className="text-[#B91C1C]">*</span></>} error={errors.min_relevance_score}>
              <select value={form.min_relevance_score} onChange={(e) => update("min_relevance_score", parseInt(e.target.value))} data-testid="project-minscore-select"
                className={`w-full px-3 py-2.5 rounded-md border bg-white text-sm ${errors.min_relevance_score ? "border-[#B91C1C] bg-[#FFF5F5]" : "border-[#E2E8F0]"}`}>
                {MIN_SCORES.map((s) => <option key={s} value={s}>{s}</option>)}
              </select>
            </Field>
          </div>
        </section>

        <div className="flex flex-wrap items-center gap-3">
          {!isOrgMember && (
            <>
              <button type="submit" disabled={busy} data-testid="project-save-button"
                className="inline-flex items-center gap-2 px-5 py-2.5 rounded-md bg-[#0F1F3D] text-white font-medium hover:bg-[#172A4F] disabled:opacity-50">
                <Save className="h-4 w-4" /> {busy ? "Saving…" : (isEdit ? "Save changes" : "Create project")}
              </button>
              <button type="button" onClick={saveAsDraft} disabled={busy} data-testid="project-save-draft-button"
                className="inline-flex items-center gap-2 px-5 py-2.5 rounded-md border border-[#E2E8F0] text-[#475569] hover:text-[#0F1F3D] hover:border-[#0F1F3D] font-medium disabled:opacity-50">
                <FileText className="h-4 w-4" /> Save as draft
              </button>
            </>
          )}

          {!isOrgMember && isEdit && (
            <>
              {form.status !== "draft" && (
                <button type="button" onClick={toggleActive} data-testid="project-pause-toggle"
                  className={`inline-flex items-center gap-2 px-5 py-2.5 rounded-md border font-medium ${form.is_active
                    ? "border-[#FDE68A] bg-[#FFFBEB] text-[#92400E] hover:bg-[#FEF3C7]"
                    : "border-[#A7F3D0] bg-[#ECFDF5] text-[#047857] hover:bg-[#D1FAE5]"
                    }`}
                >
                  {form.is_active ? <><Pause className="h-4 w-4" /> Pause project</> : <><PlayCircle className="h-4 w-4" /> Resume project</>}
                </button>
              )}
              {form.status !== "draft" && (
                <button
                  type="button"
                  onClick={() => setRunConfirmOpen(true)}
                  disabled={running || !form.is_active}
                  data-testid="project-run-button"
                  className="inline-flex items-center gap-2 px-5 py-2.5 rounded-md border border-[#0F1F3D] text-[#0F1F3D] hover:bg-[#F1F5F9] font-medium disabled:opacity-50"
                  title={
                    !form.is_active
                      ? "Resume the project to run it"
                      : running
                        ? "Pipeline is currently running"
                        : "Run the pipeline now"
                  }
                >
                  {running ? (
                    <>
                      <Loader2 className="h-4 w-4 animate-spin" />
                      Running...
                    </>
                  ) : (
                    <>
                      <Play className="h-4 w-4" />
                      Run pipeline now
                    </>
                  )}
                </button>
              )}
            </>
          )}
        </div>
        </fieldset>
      </form>

      <AIConfirmModal
        key={`${aiModal.open ? "open" : "closed"}-${aiModal.kind || ""}`}
        open={!!aiModal.open}
        mode={aiModal.mode}
        title={aiModal.title}
        originalText={aiModal.originalText}
        aiText={aiModal.aiText}
        proposals={aiModal.proposals || []}
        onCancel={() => setAiModal({ open: false })}
        onAccept={handleAiAccept}
      />

      <AutofillModal
        open={!!autofillModal.open}
        options={autofillModal.options}
        current={{ sectors: form.sectors, keywords: form.keywords }}
        onClose={() => setAutofillModal({ open: false, options: null })}
        onApply={applyAutofill}
      />

      <AlertDialog open={runConfirmOpen} onOpenChange={setRunConfirmOpen}>
        <AlertDialogContent data-testid="run-confirm-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Run this project?</AlertDialogTitle>
            <AlertDialogDescription>
              This will run the entire project pipeline and may take several minutes.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="run-confirm-cancel">Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={confirmRun} data-testid="run-confirm-button">
              Run project
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}